import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ShoppingBag, Loader2, RefreshCw, Tag, MapPin, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { useCurrency } from "@/hooks/useCurrency";

interface GiftSuggestion {
  name: string;
  description: string;
  estimatedPrice: string;
  whereToBuy: string;
  category: string;
}

interface GiftSuggestionsProps {
  recipientName: string;
  occasion: string;
  wants?: string[];
  has?: string[];
  dislikes?: string[];
  style?: string[];
}

// Budget ranges will use currency from settings - labels are rendered dynamically

const CURATED_STORES = [
  "Amazon.ae",
  "Noon",
  "Mumzworld",
  "Namshi",
  "IKEA",
  "Sharaf DG",
  "Virgin Megastore",
  "Carrefour",
  "Sephora",
  "Ounass",
];

const CATEGORY_COLORS: Record<string, string> = {
  Electronics: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  Fashion: "bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300",
  Home: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  Experience: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
  Beauty: "bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300",
  Books: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300",
  Food: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300",
  Sports: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
  Kids: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300",
};

export default function GiftSuggestions({
  recipientName,
  occasion,
  wants = [],
  has = [],
  dislikes = [],
  style = [],
}: GiftSuggestionsProps) {
  const { toast } = useToast();
  const currency = useCurrency();
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<GiftSuggestion[]>([]);
  const [selectedBudget, setSelectedBudget] = useState<string | null>(null);
  const [customBudget, setCustomBudget] = useState("");
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const [customStore, setCustomStore] = useState("");
  const [showStores, setShowStores] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);

  const BUDGET_RANGES = [
    { label: `Under 50 ${currency}`, value: `Under 50 ${currency}` },
    { label: `50–150 ${currency}`, value: `50-150 ${currency}` },
    { label: `150–300 ${currency}`, value: `150-300 ${currency}` },
    { label: `300–500 ${currency}`, value: `300-500 ${currency}` },
    { label: `500+ ${currency}`, value: `500+ ${currency}` },
  ];

  const budget = customBudget.trim() ? `Around ${customBudget.trim()} ${currency}` : selectedBudget;

  const toggleStore = (store: string) => {
    setSelectedStores((prev) =>
      prev.includes(store) ? prev.filter((s) => s !== store) : [...prev, store]
    );
  };

  const addCustomStore = () => {
    const trimmed = customStore.trim();
    if (trimmed && !selectedStores.includes(trimmed)) {
      setSelectedStores((prev) => [...prev, trimmed]);
      setCustomStore("");
    }
  };

  const handleGenerate = async () => {
    if (!budget) {
      toast({ variant: "destructive", title: "Select a budget", description: "Please choose a budget range or enter a custom amount." });
      return;
    }

    setLoading(true);
    setSuggestions([]);

    try {
      const { data, error } = await supabase.functions.invoke("suggest-gifts", {
        body: {
          recipientName,
          occasion,
          wants,
          has,
          dislikes,
          style,
          budget,
          stores: selectedStores.length > 0 ? selectedStores : undefined,
        },
      });

      if (error) throw error;

      if (data?.error) {
        toast({ variant: "destructive", title: "Error", description: data.error });
        return;
      }

      setSuggestions(data?.suggestions || []);
      setHasGenerated(true);

      if (!data?.suggestions?.length) {
        toast({ title: "No suggestions", description: "Try adjusting your budget or preferences." });
      }
    } catch (err) {
      console.error("Gift suggestion error:", err);
      toast({ variant: "destructive", title: "Error", description: "Failed to generate suggestions. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
          <Sparkles size={16} className="text-primary" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-foreground">AI Gift Suggestions</h3>
          <p className="text-xs text-muted-foreground">
            Get personalized ideas based on preferences & budget
          </p>
        </div>
      </div>

      {/* Budget Selection */}
      <div className="space-y-2.5">
        <Label className="text-sm font-medium text-foreground">Budget per gift</Label>
        <div className="flex flex-wrap gap-2">
          {BUDGET_RANGES.map((range) => (
            <button
              key={range.value}
              onClick={() => { setSelectedBudget(range.value); setCustomBudget(""); }}
              className={cn(
                "px-3 py-1.5 rounded-full text-xs font-medium border transition-all",
                selectedBudget === range.value && !customBudget
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-muted/50 text-foreground border-border hover:border-primary/50"
              )}
            >
              {range.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">or</span>
          <Input
            value={customBudget}
            onChange={(e) => { setCustomBudget(e.target.value.replace(/[^0-9]/g, "")); setSelectedBudget(null); }}
            placeholder={`Custom amount in ${currency}`}
            className="border-border text-sm max-w-[200px]"
          />
        </div>
      </div>

      {/* Store Selection */}
      <div className="space-y-2.5">
        <button
          onClick={() => setShowStores(!showStores)}
          className="flex items-center gap-2 text-sm font-medium text-foreground w-full"
        >
          <ShoppingBag size={14} className="text-muted-foreground" />
          Preferred stores
          <span className="text-xs text-muted-foreground">(optional)</span>
          <ChevronDown size={14} className={cn("ml-auto text-muted-foreground transition-transform", showStores && "rotate-180")} />
        </button>

        <AnimatePresence>
          {showStores && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden space-y-2.5"
            >
              <div className="flex flex-wrap gap-2">
                {CURATED_STORES.map((store) => (
                  <button
                    key={store}
                    onClick={() => toggleStore(store)}
                    className={cn(
                      "px-3 py-1.5 rounded-full text-xs font-medium border transition-all",
                      selectedStores.includes(store)
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-muted/50 text-foreground border-border hover:border-primary/50"
                    )}
                  >
                    {store}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={customStore}
                  onChange={(e) => setCustomStore(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomStore(); } }}
                  placeholder="Add custom store..."
                  className="border-border text-sm flex-1"
                />
                <Button variant="outline" size="sm" onClick={addCustomStore} disabled={!customStore.trim()}>
                  Add
                </Button>
              </div>
              {selectedStores.filter((s) => !CURATED_STORES.includes(s)).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {selectedStores
                    .filter((s) => !CURATED_STORES.includes(s))
                    .map((store) => (
                      <Badge key={store} variant="secondary" className="gap-1 text-xs cursor-pointer" onClick={() => toggleStore(store)}>
                        {store} ×
                      </Badge>
                    ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Generate Button */}
      <Button
        onClick={handleGenerate}
        disabled={loading || !budget}
        className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 gap-2"
      >
        {loading ? (
          <>
            <Loader2 size={16} className="animate-spin" /> Generating ideas...
          </>
        ) : hasGenerated ? (
          <>
            <RefreshCw size={16} /> Regenerate suggestions
          </>
        ) : (
          <>
            <Sparkles size={16} /> Get gift ideas
          </>
        )}
      </Button>

      {/* Results */}
      <AnimatePresence>
        {suggestions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-3"
          >
            <p className="text-xs text-muted-foreground font-medium">
              {suggestions.length} suggestions for {recipientName}
            </p>
            {suggestions.map((gift, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.08 }}
                className="bg-muted/30 border border-border rounded-xl p-4 space-y-2"
              >
                <div className="flex items-start justify-between gap-2">
                  <h4 className="text-sm font-semibold text-foreground">{gift.name}</h4>
                  <Badge className={cn("text-[10px] shrink-0", CATEGORY_COLORS[gift.category] || "bg-muted text-muted-foreground")}>
                    {gift.category}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">{gift.description}</p>
                <div className="flex items-center gap-3 text-xs">
                  <span className="flex items-center gap-1 text-foreground font-medium">
                    <Tag size={12} className="text-primary" /> {gift.estimatedPrice}
                  </span>
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <MapPin size={12} /> {gift.whereToBuy}
                  </span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
